import { Injectable } from '@angular/core';
import { IdType } from '../enums/IdType.enum';
import { IdValidationStatus } from '../enums/IdValidationStatus.enum';
import { Roles } from '../enums/Roles.enum';
import { UserStatus } from '../enums/UserStatus.enum';
import { FeatureFlagService } from './feature-flag.service';

export type LaftStatusBannerState =
  | 'firstDepositPending'
  | 'firstDepositSignatureError'
  | 'contractPendingDepositApproved'
  | 'contractPendingDepositInProcess'
  | 'depositPendingContractSigned'
  | 'validationPending'
  | 'depositPendingProfileApproved'
  | 'depositInProcessContractSigned'
  | 'readyToInvest'
  | 'signatureError'
  | 'signatureValidating'
  | 'contractPendingValidationInProgress'
  | 'contractPendingValidationDone';

export type LaftStatusBannerAction = 'deposit' | 'sign' | null;

export type LaftBannerColumn = 'preApproved' | 'noPreApproval';

type DepositStage = 'none' | 'inProcess' | 'approved';

const ACTION_BY_STATE: Record<LaftStatusBannerState, LaftStatusBannerAction> = {
  firstDepositPending: 'deposit',
  firstDepositSignatureError: 'deposit',
  depositPendingContractSigned: 'deposit',
  depositPendingProfileApproved: 'deposit',
  contractPendingDepositApproved: 'sign',
  contractPendingDepositInProcess: 'sign',
  contractPendingValidationInProgress: 'sign',
  contractPendingValidationDone: 'sign',
  signatureError: 'sign',
  validationPending: null,
  depositInProcessContractSigned: null,
  readyToInvest: null,
  signatureValidating: null,
};

const SECONDARY_ACTION_BY_STATE: Partial<
  Record<LaftStatusBannerState, LaftStatusBannerAction>
> = {
  firstDepositPending: 'sign',
  firstDepositSignatureError: 'sign',
};

@Injectable({
  providedIn: 'root',
})
export class LaftStatusBannerService {
  constructor(private featureFlagService: FeatureFlagService) {}

  isBannerDue(profile: any, laftData: any): boolean {
    if (!this.isOnLaftBannerPath(profile, laftData)) {
      return false;
    }

    if (profile?.status === UserStatus.Disapproved) {
      return false;
    }

    const dismissedTheOnlyDismissibleOne =
      profile?.preApprovalBannerClosed === true &&
      this.resolve(profile, laftData) === 'readyToInvest';

    return !dismissedTheOnlyDismissibleOne;
  }

  columnFor(profile: any, laftData: any): LaftBannerColumn | null {
    if (!this.featureFlagService.isEarlyDepositEnabled()) {
      return null;
    }

    if (profile?.role !== Roles.INVESTOR || profile?.idType === IdType.RUC) {
      return null;
    }

    if (profile?.isPreApproved === true) {
      return 'preApproved';
    }

    // `isPreApproved === false` also covers whoever never submitted the form; only a
    // not-pre-approved submit mints a `keynuaId`, so that is what separates the two.
    return this.effectiveDeclaration(profile, laftData)?.keynuaId ? 'noPreApproval' : null;
  }

  // `getLaftData()` only runs on load while the account is `email verified`, and signing
  // ends that status — so the profile's copy is the one that survives a reload (E1).
  effectiveDeclaration(profile: any, laftData: any): any {
    return laftData ?? profile?.laftDeclarationSummary ?? null;
  }

  isOnLaftBannerPath(profile: any, laftData: any): boolean {
    return this.columnFor(profile, laftData) !== null;
  }

  // ⚠ The account status, and nothing else. Reading the declaration instead answered
  // "a verdict arrived" — `airtableLaftId` is set by the FIRST webhook whatever it said —
  // so a disapproved or still-pending account rendered *Perfil aprobado* in green.
  isValidationDone(profile: any): boolean {
    return profile?.status === UserStatus.Approved;
  }

  // FIN-1736 · either answer ends the validation: an admin approving the account, which
  // never waits for the signature, or compliance validating the declaration (D118).
  isLaftValidationApproved(profile: any, declaration: any): boolean {
    return (
      this.isValidationDone(profile) ||
      (declaration?.idValidation === IdValidationStatus.Valid && !!declaration?.airtableLaftId)
    );
  }

  resolve(profile: any, laftData: any): LaftStatusBannerState {
    const declaration = this.effectiveDeclaration(profile, laftData);
    const deposit = this.depositStage(profile);
    const signed = declaration?.keynuaSignedContract === true;

    if (profile?.hasLaftProfileError) {
      if (!this.isPreApprovedFirstDeposit(profile, declaration, deposit)) {
        return 'signatureError';
      }

      if (!signed) {
        return 'firstDepositSignatureError';
      }
    }

    if (declaration?.keynuaWidgetSigned && !declaration?.keynuaSignedContract) {
      return 'signatureValidating';
    }

    if (this.columnFor(profile, declaration) === 'noPreApproval') {
      return this.resolveNoPreApproval(
        signed,
        this.isLaftValidationApproved(profile, declaration),
        deposit,
      );
    }

    const approved = profile?.status === UserStatus.Approved;

    if (!signed) {
      if (approved) {
        return 'depositPendingContractSigned';
      }

      if (deposit === 'approved') {
        return 'contractPendingDepositApproved';
      }

      return deposit === 'inProcess'
        ? 'contractPendingDepositInProcess'
        : 'firstDepositPending';
    }

    if (deposit === 'inProcess') {
      return 'depositInProcessContractSigned';
    }

    if (deposit === 'approved') {
      return approved ? 'readyToInvest' : 'validationPending';
    }

    return approved ? 'depositPendingProfileApproved' : 'depositPendingContractSigned';
  }

  private resolveNoPreApproval(
    signed: boolean,
    validationDone: boolean,
    deposit: DepositStage,
  ): LaftStatusBannerState {
    if (!signed) {
      return validationDone
        ? 'contractPendingValidationDone'
        : 'contractPendingValidationInProgress';
    }

    if (!validationDone) {
      return 'validationPending';
    }

    if (deposit === 'inProcess') {
      return 'depositInProcessContractSigned';
    }

    return deposit === 'approved' ? 'readyToInvest' : 'depositPendingProfileApproved';
  }

  actionFor(state: LaftStatusBannerState): LaftStatusBannerAction {
    return ACTION_BY_STATE[state] ?? null;
  }

  secondaryActionFor(state: LaftStatusBannerState): LaftStatusBannerAction {
    return SECONDARY_ACTION_BY_STATE[state] ?? null;
  }

  private isPreApprovedFirstDeposit(
    profile: any,
    declaration: any,
    deposit: DepositStage,
  ): boolean {
    return (
      this.columnFor(profile, declaration) === 'preApproved' &&
      profile?.status !== UserStatus.Approved &&
      deposit === 'none'
    );
  }

  private depositStage(profile: any): DepositStage {
    if (profile?.hasDeposit === true) {
      return 'approved';
    }

    return profile?.hasCountedDeposit === true ? 'inProcess' : 'none';
  }
}
